import {
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  type UserCredential,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "./firebase";
import { trackSignUp } from "./gtmService";

const googleProvider = new GoogleAuthProvider();

const functions = getFunctions();
const syncAuthProfileToFirestore = httpsCallable(
  functions,
  "syncAuthProfileToFirestore",
);
const linkedinAuthInit = httpsCallable(functions, "linkedinAuthInit");
const syncLinkedInProfileToFirestore = httpsCallable(
  functions,
  "syncLinkedInProfileToFirestore",
);

export const signUpWithEmail = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
): Promise<UserCredential> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const { user } = userCredential;

    // Update profile with display name
    await updateProfile(user, {
      displayName: `${firstName} ${lastName}`,
    });

    // Send email verification
    try {
      const actionCodeSettings = {
        url: getVerificationContinueUrl(),
        handleCodeInApp: false, // Keep default hosting page for verification
      };
      await sendEmailVerification(user, actionCodeSettings);
      console.log("Email verification sent successfully.");
    } catch (verificationError) {
      console.error("Error sending email verification:", verificationError);
      // Continue with signup even if email verification fails
    }

    // Call the Cloud Function to sync profile to Firestore
    try {
      await syncAuthProfileToFirestore();
      console.log(
        "Successfully triggered profile sync for new email/password user.",
      );
    } catch (syncError) {
      console.error(
        "Error triggering profile sync for new email/password user:",
        syncError,
      );
      // Optionally re-throw or handle, for now just log
    }

    // Track signup event in GTM
    trackSignUp({ method: "email" }).catch((err) =>
      console.error("Failed to track signup:", err),
    );

    return userCredential;
  } catch (error) {
    console.error("Error signing up with email:", error);
    throw error;
  }
};

export const signInWithEmail = async (
  email: string,
  password: string,
): Promise<UserCredential> => {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error) {
    console.error("Error signing in with email:", error);
    throw error;
  }
};

export const signInWithGoogle = async (): Promise<UserCredential> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);

    // Track signup event if this is a new user
    const additionalInfo = getAdditionalUserInfo(result);
    if (additionalInfo?.isNewUser) {
      trackSignUp({ method: "google" }).catch((err) =>
        console.error("Failed to track signup:", err),
      );
    }

    // Call the Cloud Function to sync profile to Firestore
    try {
      await syncAuthProfileToFirestore();
      console.log(
        "Successfully triggered profile sync for Google sign-in user.",
      );
    } catch (syncError) {
      console.error(
        "Error triggering profile sync for Google sign-in user:",
        syncError,
      );
      // Optionally re-throw or handle, for now just log
    }

    return result;
  } catch (error) {
    console.error("Error signing in with Google:", error);
    throw error;
  }
};

export const signInWithLinkedIn = async (): Promise<UserCredential> => {
  try {
    // 1. Initialize OAuth flow
    const { data } = await linkedinAuthInit();
    const { authorizationUrl } = data as { authorizationUrl: string };

    // 2. Open popup window
    const popup = window.open(
      authorizationUrl,
      "LinkedIn Sign In",
      "width=600,height=700,scrollbars=yes",
    );

    if (!popup) {
      throw new Error("Popup blocked. Please allow popups for this site.");
    }

    // 3. Listen for callback message
    const customToken = await new Promise<string>((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error("Authentication timeout"));
      }, 300000); // 5 minutes

      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data.type !== "LINKEDIN_AUTH_RESULT") return;

        cleanup();

        if (event.data.error) {
          reject(new Error(event.data.error));
        } else if (event.data.token) {
          resolve(event.data.token);
        } else {
          reject(new Error("No token received"));
        }
      };

      const cleanup = () => {
        clearTimeout(timeout);
        window.removeEventListener("message", messageHandler);
        if (popup && !popup.closed) {
          popup.close();
        }
      };

      window.addEventListener("message", messageHandler);
    });

    // 4. Sign in with custom token
    const result = await signInWithCustomToken(auth, customToken);

    // Track signup event if this is a new user
    const additionalInfo = getAdditionalUserInfo(result);
    if (additionalInfo?.isNewUser) {
      trackSignUp({ method: "linkedin" }).catch((err) =>
        console.error("Failed to track signup:", err),
      );
    }

    // 5. Sync profile to Firestore
    try {
      await syncLinkedInProfileToFirestore();
      console.log("Successfully synced LinkedIn profile to Firestore");
    } catch (syncError) {
      console.error("Error syncing LinkedIn profile:", syncError);
      // Don't throw - authentication succeeded
    }

    return result;
  } catch (error) {
    console.error("Error signing in with LinkedIn:", error);
    throw error;
  }
};

export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error("Error signing out:", error);
    throw error;
  }
};

const getVerificationContinueUrl = (): string => {
  // Always redirect to the email-verification page.
  const currentUrl = window.location.origin;
  return `${currentUrl}/verify-email`;
};

export const sendVerificationEmail = async (): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("No user is currently signed in");
    }

    const actionCodeSettings = {
      url: getVerificationContinueUrl(),
      handleCodeInApp: false, // Keep default hosting page for verification
    };

    await sendEmailVerification(user, actionCodeSettings);
    console.log("Verification email sent successfully.");
  } catch (error) {
    console.error("Error sending verification email:", error);
    throw error;
  }
};

export const checkEmailVerified = (): boolean => {
  const user = auth.currentUser;
  return user?.emailVerified ?? false;
};

export const resetPassword = async (email: string): Promise<void> => {
  try {
    const actionCodeSettings = {
      url: `${window.location.origin}/login`,
      handleCodeInApp: false,
    };

    await sendPasswordResetEmail(auth, email, actionCodeSettings);
    console.log("Password reset email sent successfully.");
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
};
